<template>
  <div class="signup-wrapper">
    <div class="signup-grid">
      <Card class="signup-card">
        <template #title>
          <div class="title-row">
            <i class="pi pi-building" />
            <span>Registro de Empresa</span>
          </div>
        </template>
        <template #subtitle>
          Crea tu empresa y continúa al pago de suscripción.
        </template>
        <template #content>
          <form class="signup-form" @submit.prevent="submitSignup">
            <div class="field">
              <label for="company_name">Nombre de la empresa</label>
              <InputText id="company_name" v-model="form.company_name" :class="{ 'p-invalid': errors.company_name }" />
              <small v-if="errors.company_name" class="p-error">{{ errors.company_name }}</small>
            </div>

            <div class="field">
              <label for="company_email">Correo de la empresa</label>
              <InputText id="company_email" v-model="form.company_email" type="email" :class="{ 'p-invalid': errors.company_email }" />
              <small v-if="errors.company_email" class="p-error">{{ errors.company_email }}</small>
            </div>

            <div class="field">
              <label for="admin_name">Nombre del administrador</label>
              <InputText id="admin_name" v-model="form.admin_name" :class="{ 'p-invalid': errors.admin_name }" />
              <small v-if="errors.admin_name" class="p-error">{{ errors.admin_name }}</small>
            </div>

            <div class="field">
              <label for="admin_email">Correo del administrador</label>
              <InputText id="admin_email" v-model="form.admin_email" type="email" :class="{ 'p-invalid': errors.admin_email }" />
              <small v-if="errors.admin_email" class="p-error">{{ errors.admin_email }}</small>
            </div>

            <div class="field">
              <label for="password">Contraseña</label>
              <Password
                id="password"
                v-model="form.password"
                toggleMask
                :feedback="true"
                :class="{ 'p-invalid': errors.password }"
              />
              <small v-if="errors.password" class="p-error">{{ errors.password }}</small>
            </div>

            <div class="field">
              <label for="confirm_password">Confirmar contraseña</label>
              <Password
                id="confirm_password"
                v-model="form.confirm_password"
                toggleMask
                :feedback="false"
                :class="{ 'p-invalid': errors.confirm_password }"
              />
              <small v-if="errors.confirm_password" class="p-error">{{ errors.confirm_password }}</small>
            </div>

            <Button
              type="submit"
              class="w-full"
              :loading="loading"
              :label="selectedPlan ? `Registrarme y suscribirme a ${selectedPlan.name}` : 'Registrar empresa'"
              icon="pi pi-check"
            />

            <div class="login-link">
              ¿Ya tienes cuenta?
              <RouterLink to="/login">Iniciar sesión</RouterLink>
            </div>
          </form>
        </template>
      </Card>

      <Card class="plan-card">
        <template #title>
          Plan seleccionado
        </template>
        <template #content>
          <div v-if="selectedPlan" class="plan-content">
            <h3>{{ selectedPlan.name }}</h3>
            <p class="price">${{ (selectedPlan.monthly_price / 100).toLocaleString('es-CO') }} COP/mes</p>
            <ul>
              <li>{{ selectedPlan.included_tokens?.toLocaleString() || 'Ilimitados' }} tokens/mes</li>
              <li>{{ selectedPlan.max_agents ?? 'Ilimitados' }} agentes</li>
              <li>Soporte {{ selectedPlan.support_level }}</li>
            </ul>
          </div>
          <div v-else class="plan-content">
            <p>No se recibió plan en la URL.</p>
            <p>Después del registro podrás elegir un plan desde la plataforma.</p>
          </div>
        </template>
      </Card>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, reactive, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useToast } from 'primevue/usetoast';
import Card from 'primevue/card';
import InputText from 'primevue/inputtext';
import Password from 'primevue/password';
import Button from 'primevue/button';
import api from '../services/api';
import { useAuthStore } from '../stores/auth';

const route = useRoute();
const router = useRouter();
const toast = useToast();
const authStore = useAuthStore();

const loading = ref(false);
const plans = ref([]);

const form = reactive({
  company_name: '',
  company_email: '',
  admin_name: '',
  admin_email: '',
  password: '',
  confirm_password: '',
});

const errors = reactive({
  company_name: '',
  company_email: '',
  admin_name: '',
  admin_email: '',
  password: '',
  confirm_password: '',
});

const selectedPlanId = computed(() => {
  const queryPlan = Number(route.query.plan);
  return Number.isFinite(queryPlan) && queryPlan > 0 ? queryPlan : null;
});

const selectedPlan = computed(() => {
  if (!selectedPlanId.value) return null;
  return plans.value.find((p) => p.id === selectedPlanId.value) || null;
});

function resetErrors() {
  errors.company_name = '';
  errors.company_email = '';
  errors.admin_name = '';
  errors.admin_email = '';
  errors.password = '';
  errors.confirm_password = '';
}

function isValidEmail(email) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

function validateForm() {
  resetErrors();
  let valid = true;

  if (!form.company_name.trim()) {
    errors.company_name = 'El nombre de la empresa es obligatorio';
    valid = false;
  }
  if (!form.company_email.trim() || !isValidEmail(form.company_email)) {
    errors.company_email = 'Ingresa un correo de empresa válido';
    valid = false;
  }
  if (!form.admin_name.trim()) {
    errors.admin_name = 'El nombre del administrador es obligatorio';
    valid = false;
  }
  if (!form.admin_email.trim() || !isValidEmail(form.admin_email)) {
    errors.admin_email = 'Ingresa un correo de administrador válido';
    valid = false;
  }
  if (!form.password || form.password.length < 8) {
    errors.password = 'La contraseña debe tener al menos 8 caracteres';
    valid = false;
  }
  if (form.confirm_password !== form.password) {
    errors.confirm_password = 'Las contraseñas no coinciden';
    valid = false;
  }

  return valid;
}

async function fetchPlans() {
  try {
    const response = await api.get('/payments/plans');
    plans.value = response.data;
  } catch (error) {
    console.error('Error loading plans:', error);
  }
}

async function loginAfterRegister(payload) {
  authStore.setToken(payload.access_token);
  authStore.setRefreshToken(payload.refresh_token);
  authStore.setUser(payload.user);

  try {
    const permissionsResponse = await api.get('/auth/users/me/permissions');
    authStore.setPermissions(permissionsResponse.data.permissions || []);
  } catch (error) {
    console.error('Error loading permissions after register:', error);
    authStore.setPermissions([]);
  }
}

async function continueToSubscription() {
  if (!selectedPlanId.value) {
    router.push('/plans');
    return;
  }

  const response = await api.post(`/payments/subscribe/${selectedPlanId.value}`);
  const paymentUrl = response.data?.payment_link_url;
  if (!paymentUrl) {
    throw new Error('No se recibió URL de pago');
  }
  window.location.href = paymentUrl;
}

async function submitSignup() {
  if (!validateForm()) return;

  loading.value = true;
  try {
    const registerResponse = await api.post('/auth/register-company', {
      company_name: form.company_name.trim(),
      company_email: form.company_email.trim(),
      admin_name: form.admin_name.trim(),
      admin_email: form.admin_email.trim(),
      password: form.password,
      plan_id: selectedPlanId.value,
    });

    await loginAfterRegister(registerResponse.data);
    await continueToSubscription();
  } catch (error) {
    console.error('Error registering company:', error);
    toast.add({
      severity: 'error',
      summary: 'No se pudo completar el registro',
      detail: error.response?.data?.detail || 'Verifica la información e intenta de nuevo',
      life: 5000,
    });
  } finally {
    loading.value = false;
  }
}

onMounted(fetchPlans);
</script>

<style scoped>
.signup-wrapper {
  min-height: 100vh;
  padding: 2rem 1rem;
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
}

.signup-grid {
  max-width: 1100px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: minmax(320px, 1.2fr) minmax(280px, 1fr);
  gap: 1rem;
}

.signup-card,
.plan-card {
  border-radius: 14px;
  box-shadow: 0 10px 30px rgba(2, 6, 23, 0.08);
}

.title-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.signup-form {
  display: grid;
  gap: 0.85rem;
}

.field {
  display: grid;
  gap: 0.25rem;
}

.field label {
  font-size: 0.9rem;
  color: #1f2937;
  font-weight: 600;
}

.plan-content h3 {
  margin: 0;
  color: #0f172a;
}

.price {
  margin: 0.35rem 0 0.75rem;
  color: #2563eb;
  font-weight: 700;
  font-size: 1.25rem;
}

.plan-content ul {
  margin: 0;
  padding-left: 1rem;
  color: #334155;
}

.login-link {
  margin-top: 0.35rem;
  text-align: center;
  color: #475569;
  font-size: 0.88rem;
}

.login-link a {
  margin-left: 0.35rem;
  text-decoration: none;
}

@media (max-width: 900px) {
  .signup-grid {
    grid-template-columns: 1fr;
  }
}
</style>
